import React, { useEffect, useRef, useState } from 'react';
import { BiErrorCircle } from 'react-icons/bi';
import { IoMdArrowDropdown } from 'react-icons/io';

import { Popover, Tooltip } from 'antd';
import EditableCell from 'components/common/EditableCell';
import NumericInputField from 'components/common/NumericInputField';
import {
  CHANGE_SEED_TARGET_MENU_LABEL,
  OTHER_COST_MODE_PER_HA_LABEL,
  OTHER_COST_MODE_TOTAL_LABEL,
  OTHER_COST_PER_HA_SUFFIX,
  PRODUCTION_COST_PER_HA_LABEL,
  SEED_TARGET_MISSING_TOOLTIP,
  TASKS_GRID_COLUMN_LABEL_REQUIRED,
  TASKS_GRID_REORDER_STEP_ARIA_LABEL_PREFIX,
  TASKS_GRID_SELECT_UNIT_PLACEHOLDER,
  TASKS_GRID_STEP_ACTIONS_ARIA_LABEL,
  TASKS_SHEET_EMPTY_VALUE,
  TASKS_SHEET_PER_UNIT_SUFFIX,
  YIELD_LINKED_PER_UNIT_LABEL,
} from 'constants/uiConstants';
import { useSelectedInput } from 'hooks/inputs/useSelectedInput';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import {
  IInputStep,
  IInputStepSeed,
  IInputStepSeedDress,
  InputStepType,
  SeedDressRateMode,
} from 'interfaces/IInputStep';
import type { IInputTask } from 'interfaces/IInputTask';
import { formatCurrency } from 'utils/CurrencyUtils';
import MergeClassNames from 'utils/MergeClassNames';

import {
  focusAdjacentStepCell,
  STEP_NAV_COL_ATTR,
  STEP_NAV_COL_COST,
  STEP_NAV_COL_RATE,
} from './focusAdjacentStepCell';
import {
  getOtherCostAmount,
  getOtherCostAreaHa,
  isOtherCostTask,
  OtherCostEntryMode,
  toOtherCostPerHa,
} from './otherCosts';
import SeedTargetList from './SeedTargetList';
import { setSheetRowDragImage } from './sheetDragImage';
import type { FoldedColumns } from './sheetLayout';
import {
  buildSeedLinkedReason,
  calculateStepCostPerArea,
  calculateStepRequired,
  formatStepCalculation,
  getLinkedSeedDressItems,
  getRateUnitOptions,
  getStepCost,
  getStepDisplayLabel,
  getStepRate,
  getStepRateUnitDisplayLabel,
  NO_CALCULATION_STEP_TYPES,
} from './stepCalculations';
import { getStepSelectorGroupsForTask } from './stepConfig';
import { StepSelection } from './stepFactory';
import StepInputSelector from './StepInputSelector';
import { isFocusLeftWithPicker, TASK_STEP_ROW_ATTR } from './taskEntryFocus';
import { RowGripButton, RowKebabButton } from './TaskRowContextMenu';
import type { SheetColumnVisibility } from './tasksViewPreferences';
import type { useStepContextMenu } from './useStepContextMenu';
import type { useStepReorder } from './useStepReorder';
import {
  getProductionCostBasisPatch,
  getProductionStep,
  isProductionCostStep,
  isYieldLinkedStep,
  ProductionCostBasis,
} from './yieldLinkedCosts';

type StepReorderApi = ReturnType<typeof useStepReorder>;
type StepContextMenuApi = ReturnType<typeof useStepContextMenu>;

interface TaskStepRowProps {
  task: IInputTask;
  step: IInputStep;
  isLastRow: boolean;
  /** The product's analysis in a focused filter view (nutrients or MoA). */
  detailLine?: string | null;
  /** Rendered before the product cells: the category sheet's Task cell (OP-2556). */
  leadingCell?: React.ReactNode;
  /**
   * Set in the category sheet (OP-2556): which value columns show, with
   * Required before Cost /unit so the cost columns sit together, and the
   * grip and three-dot button inside the Input name cell so the grid runs
   * edge to edge.
   */
  sheetColumns?: SheetColumnVisibility;
  /**
   * Columns folded away for width, in the sheet or the original layout
   * (OP-2556): they don't render, and their values show on a small line
   * under the product name instead, so nothing is lost.
   */
  foldedColumns?: FoldedColumns;
  /** Rendered after the product cells: the sheet's Notes column. */
  trailingCell?: React.ReactNode;
  hasAmbiguousStepIds: boolean;
  canReorder: boolean;
  gUnits: GUnit[];
  editingStepInputId: string | null;
  setEditingStepInputId: (stepId: string | null) => void;
  handleStepInputChange: (
    task: IInputTask,
    stepId: string,
    selections: StepSelection[]
  ) => void;
  commitStepField: (
    task: IInputTask,
    stepId: string,
    patch: Partial<IInputStep>
  ) => void;
  stepReorder: StepReorderApi;
  stepContextMenu: StepContextMenuApi;
}

// One row of the step sub-table: the "change input" popover, Rate/Rate
// unit/Seed-to-be-treated/Cost/Required/$-ha cells, the re-order grip and
// drag-and-drop wiring, and the row's own kebab actions button.
const TaskStepRow: React.FC<TaskStepRowProps> = ({
  task,
  step,
  isLastRow,
  detailLine,
  leadingCell,
  sheetColumns,
  foldedColumns,
  trailingCell,
  hasAmbiguousStepIds,
  canReorder,
  gUnits,
  editingStepInputId,
  setEditingStepInputId,
  handleStepInputChange,
  commitStepField,
  stepReorder,
  stepContextMenu,
}) => {
  const { reorderTarget, armedDragStepId, dragOverStepId } = stepReorder;
  const { contextMenuState, seedTargetEditTarget } = stepContextMenu;
  const selectedInput = useSelectedInput();

  const isEditingSeedTarget =
    seedTargetEditTarget?.taskId === task.taskId &&
    seedTargetEditTarget?.stepId === step.stepId;
  const isPickerOpen =
    editingStepInputId === step.stepId || isEditingSeedTarget;
  // How a other cost's amount is entered; not saved, as it's stored as $/ha.
  const [otherCostEntryMode, setOtherCostEntryMode] =
    useState<OtherCostEntryMode>('total');

  // The picker is removed from the page when it closes (Esc, a choice, or a
  // click away), taking focus with it, so Tab would start again from the top
  // of the page. When that happens, focus comes back to this product's name
  // cell, so Tab carries on to its Rate as the user expects.
  const nameTriggerRef = useRef<HTMLDivElement>(null);
  const [isHoverSuppressed, setIsHoverSuppressed] = useState(false);
  const wasPickerOpenRef = useRef(isPickerOpen);
  useEffect(() => {
    if (wasPickerOpenRef.current && !isPickerOpen) {
      // The pointer usually still rests on the cell, so its hover outline
      // would stay lit after the user Tabs on — reading as a stuck focus.
      // It stays off until the pointer moves again.
      setIsHoverSuppressed(true);
      const frame = requestAnimationFrame(() => {
        if (isFocusLeftWithPicker()) nameTriggerRef.current?.focus();
      });
      wasPickerOpenRef.current = isPickerOpen;
      return () => cancelAnimationFrame(frame);
    }
    wasPickerOpenRef.current = isPickerOpen;
  }, [isPickerOpen]);

  const isReordering =
    reorderTarget?.taskId === task.taskId &&
    reorderTarget.stepId === step.stepId;
  // Suppressed when stepIds repeat: comparing by id alone would light up
  // every row that shares it, misleading the user about which row they
  // targeted.
  const isHighlighted =
    !hasAmbiguousStepIds &&
    contextMenuState?.target.rowType === 'step' &&
    contextMenuState.target.taskId === task.taskId &&
    contextMenuState.target.stepId === step.stepId;
  // Only ever set for a row in the dragged step's own task, so the id alone
  // identifies the target.
  const isDropTarget = dragOverStepId === step.stepId;
  const rate = getStepRate(step);
  const cost = getStepCost(step);
  const required = selectedInput
    ? calculateStepRequired(step, task, selectedInput, gUnits)
    : null;
  const costPerArea = calculateStepCostPerArea(step, task, gUnits);
  const stepDisplayLabel = getStepDisplayLabel(
    step as IInputStepSeed | IInputStepSeedDress,
    task
  );
  const isMissingSeedTarget =
    step.stepType === InputStepType.SeedDress &&
    step.rateMode === SeedDressRateMode.Per100Kg &&
    !step.linkedStepId;
  // When this is a Seed step linked by at least one SeedDress, switching step
  // type in the picker would leave those treatments with a dangling reference.
  // Compute a human-readable reason naming the specific blocking treatments so
  // the locked category-tab tooltip tells the user exactly what to delete first.
  const seedLinkedTypeChangeReason = (() => {
    if (step.stepType !== InputStepType.Seed) return undefined;
    const items = getLinkedSeedDressItems(task, step.stepId);
    return items.length > 0
      ? buildSeedLinkedReason(items, ' to change step type')
      : undefined;
  })();
  const cellBorder = MergeClassNames(
    'border-gray-medium',
    !isLastRow && 'border-b'
  );
  const inputNameCellBorder = MergeClassNames(
    isMissingSeedTarget
      ? 'bg-information border-gray-medium'
      : 'border-gray-medium',
    !isLastRow && 'border-b'
  );
  const isSheet = !!sheetColumns;
  // Calculated and blank cells are filled grey, so it's plain they can't be
  // edited (and why Tab and the arrow keys pass them by); the sheet uses its
  // lightest grey, shared with the total rows (OP-2556).
  const readOnlyFill = isSheet
    ? 'bg-rowHover text-gray-dark'
    : 'bg-secondary-light';
  // Cells an input doesn't use show a dash in the sheet, not an empty box.
  const blankValue = isSheet ? (
    <span className="text-gray-muted">{TASKS_SHEET_EMPTY_VALUE}</span>
  ) : (
    ' '
  );
  // The folded columns' values, e.g. "kg · Required 28.80 · $0.09/unit".
  const foldedLine = foldedColumns
    ? [
        foldedColumns.rateUnit && !NO_CALCULATION_STEP_TYPES.has(step.stepType)
          ? getStepRateUnitDisplayLabel(step).trim()
          : null,
        foldedColumns.required && required !== null
          ? `${TASKS_GRID_COLUMN_LABEL_REQUIRED} ${formatStepCalculation(required)}`
          : null,
        foldedColumns.costPerUnit && cost !== null
          ? `${formatCurrency(cost)}${
              // A other cost's cost is $/ha; a yield-linked cost's is per
              // unit of yield, e.g. $/t (OP-2399).
              isOtherCostTask(task)
                ? OTHER_COST_PER_HA_SUFFIX
                : isYieldLinkedStep(step, task) && step.appUnit?.unit
                  ? YIELD_LINKED_PER_UNIT_LABEL(step.appUnit.unit)
                  : TASKS_SHEET_PER_UNIT_SUFFIX
            }`
          : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';
  const nameDetailLine = [detailLine, foldedLine].filter(Boolean).join(' · ');
  const isOperationRow = NO_CALCULATION_STEP_TYPES.has(step.stepType);
  const defaultsNote = stepContextMenu.defaultsNotes[step.stepId];
  // A other cost (OP-2399) is entered as a total or $/ha and stored as $/ha
  // at rate 1; a total needs the plan's area to spread over.
  const isOtherCostRow =
    isOtherCostTask(task) && step.stepType === InputStepType.Application;
  const otherCostAreaHa =
    isOtherCostRow && selectedInput
      ? getOtherCostAreaHa(step, task, selectedInput, gUnits)
      : 0;
  const otherCostMode: OtherCostEntryMode =
    otherCostAreaHa > 0 ? otherCostEntryMode : 'perHa';
  const otherCostModeOptions = [
    ...(otherCostAreaHa > 0
      ? [{ value: 'total', label: OTHER_COST_MODE_TOTAL_LABEL }]
      : []),
    { value: 'perHa', label: OTHER_COST_MODE_PER_HA_LABEL },
  ];
  // An Operation cost on a Production task with a product: $/ha or per unit
  // of its yield (OP-2399).
  const productionCostYieldUnit = isProductionCostStep(step, task)
    ? (getProductionStep(task)?.yieldUnit?.unit ?? null)
    : null;
  const productionCostBasis: ProductionCostBasis = isYieldLinkedStep(step, task)
    ? 'perYield'
    : 'perHa';
  const otherCostAmount = isOtherCostRow
    ? getOtherCostAmount(cost, otherCostMode, otherCostAreaHa)
    : null;
  const hasCost = 'cost' in step;
  const currentAppUnit = 'appUnit' in step ? (step.appUnit ?? null) : null;
  const stepInputCategoryKey = getStepSelectorGroupsForTask(task.taskType).find(
    (group) => group.stepType === step.stepType
  )?.key;

  const renderStepDisplayLabel = stepDisplayLabel && (
    <div className="flex flex-row items-center gap-1">
      <div>{stepDisplayLabel.primaryLabel}</div>
      {stepDisplayLabel.secondaryLabel && (
        <div className="text-xs font-semibold text-gray-muted">
          {`(${stepDisplayLabel.secondaryLabel})`}
        </div>
      )}
    </div>
  );

  const kebabButton = (
    <RowKebabButton
      label={TASKS_GRID_STEP_ACTIONS_ARIA_LABEL}
      isActive={isHighlighted}
      onOpen={(event) =>
        stepContextMenu.openMenuFromKebab(event, {
          rowType: 'step',
          taskId: task.taskId,
          stepId: step.stepId,
        })
      }
    />
  );
  const costPerUnitCell = (
    <td
      className={MergeClassNames('relative border-r p-0', cellBorder)}
      {...{ [STEP_NAV_COL_ATTR]: STEP_NAV_COL_COST }}
    >
      <div className="absolute inset-0">
        {isOtherCostRow ? (
          <NumericInputField
            className="h-full w-full"
            inputClassName="h-full w-full"
            min={0}
            inclusiveMin
            allowEmpty
            disabled={hasAmbiguousStepIds}
            value={otherCostAmount}
            displayValue={
              otherCostAmount === null ? ' ' : formatCurrency(otherCostAmount)
            }
            onArrowNavigate={focusAdjacentStepCell}
            onCommit={(value) =>
              commitStepField(task, step.stepId, {
                rate: 1,
                // Clears a catalogue "$/ha" unit from costs added earlier.
                appUnit: null,
                cost: toOtherCostPerHa(value, otherCostMode, otherCostAreaHa),
              })
            }
          />
        ) : hasCost ? (
          <NumericInputField
            className="h-full w-full"
            inputClassName="h-full w-full"
            min={0}
            inclusiveMin
            allowEmpty
            disabled={hasAmbiguousStepIds}
            value={cost}
            displayValue={cost === null ? ' ' : formatCurrency(cost)}
            onArrowNavigate={focusAdjacentStepCell}
            onCommit={(value) =>
              commitStepField(task, step.stepId, { cost: value })
            }
          />
        ) : (
          <div
            className={MergeClassNames(
              'flex h-full w-full items-center border border-transparent px-2 py-1 font-medium',
              readOnlyFill
            )}
          >
            {blankValue}
          </div>
        )}
      </div>
    </td>
  );
  const requiredCell = (
    <td className={MergeClassNames('relative border-r p-0', cellBorder)}>
      {
        <div
          className={MergeClassNames(
            'absolute inset-0 flex items-center border border-transparent px-2 py-1 text-sm font-semibold',
            readOnlyFill
          )}
        >
          {formatStepCalculation(required)}
        </div>
      }
    </td>
  );

  const rateUnitCell = (
    <td className={MergeClassNames('relative border-r p-0', cellBorder)}>
      <div className="absolute inset-0">
        {isOtherCostRow ? (
          <EditableCell
            type="select"
            className="h-full w-full"
            disabled={hasAmbiguousStepIds}
            value={otherCostMode}
            displayValue={
              otherCostMode === 'total'
                ? OTHER_COST_MODE_TOTAL_LABEL
                : OTHER_COST_MODE_PER_HA_LABEL
            }
            options={otherCostModeOptions}
            onCommit={(mode) => {
              if (mode === 'total' || mode === 'perHa') {
                setOtherCostEntryMode(mode);
              }
            }}
          />
        ) : productionCostYieldUnit ? (
          // An Operation cost on a Production task is $/ha or per unit of
          // yield, e.g. "/t" (OP-2399).
          <EditableCell
            type="select"
            className="h-full w-full"
            disabled={hasAmbiguousStepIds}
            value={productionCostBasis}
            displayValue={
              productionCostBasis === 'perYield'
                ? YIELD_LINKED_PER_UNIT_LABEL(productionCostYieldUnit)
                : PRODUCTION_COST_PER_HA_LABEL
            }
            options={[
              { value: 'perHa', label: PRODUCTION_COST_PER_HA_LABEL },
              {
                value: 'perYield',
                label: YIELD_LINKED_PER_UNIT_LABEL(productionCostYieldUnit),
              },
            ]}
            onCommit={(basis) => {
              if (
                (basis === 'perHa' || basis === 'perYield') &&
                basis !== productionCostBasis
              ) {
                commitStepField(
                  task,
                  step.stepId,
                  getProductionCostBasisPatch(basis, task)
                );
              }
            }}
          />
        ) : isOperationRow ? (
          <div
            className={MergeClassNames(
              'flex h-full w-full items-center border border-transparent px-2 py-1 font-medium',
              readOnlyFill
            )}
          >
            {blankValue}
          </div>
        ) : (
          <EditableCell
            type="select"
            className="h-full w-full"
            disabled={hasAmbiguousStepIds}
            value={currentAppUnit?.globalId ?? null}
            displayValue={getStepRateUnitDisplayLabel(step)}
            options={getRateUnitOptions(step.stepType, gUnits)}
            placeholder={TASKS_GRID_SELECT_UNIT_PLACEHOLDER}
            onCommit={(globalId) => {
              const selected = gUnits.find(
                (unit) => unit.globalId === globalId
              );
              if (!selected) return;
              commitStepField(task, step.stepId, {
                appUnit: { unit: selected.code, globalId: selected.globalId },
              });
            }}
          />
        )}
      </div>
    </td>
  );
  const costPerHaCell = (
    <td className={MergeClassNames('relative border-r p-0', cellBorder)}>
      {
        <div
          className={MergeClassNames(
            'absolute inset-0 flex items-center justify-end border border-transparent px-2 py-1 text-sm font-semibold',
            readOnlyFill
          )}
        >
          {costPerArea === null ? ' ' : formatCurrency(costPerArea)}
        </div>
      }
      {/* Sibling of the value div, not a child of it: positioned in the
          wrapper's own pr-7 lane, right of the grid's border — mirrors the
          grip handle's lane on the opposite side, so the fill isn't
          squeezed to make room for the button anymore (design reference:
          the dots sit outside the table, not inside its last column).
          The category sheet has no lane: its button is in Input name. */}
      {!isSheet && (
        <div className="absolute inset-y-0 -right-6 z-10 flex w-6 items-center justify-center">
          {kebabButton}
        </div>
      )}
    </td>
  );

  const nameCellContent = (
    <>
      <div className="block w-full min-w-0 flex-1">
        <div className="truncate">{renderStepDisplayLabel}</div>
        {nameDetailLine && (
          <div className="truncate text-xs font-normal text-gray-dark">
            {nameDetailLine}
          </div>
        )}
        {defaultsNote && (
          <div
            role="status"
            className={MergeClassNames(
              'truncate text-xs font-semibold',
              defaultsNote.isError
                ? 'text-danger-dark'
                : 'mt-0.5 w-fit max-w-full rounded-full bg-primary-dark px-2 py-0.5 text-white'
            )}
          >
            {defaultsNote.text}
          </div>
        )}
      </div>
      {isMissingSeedTarget && (
        <Tooltip title={SEED_TARGET_MISSING_TOOLTIP}>
          <span className="ml-1 flex flex-shrink-0 items-center">
            <BiErrorCircle
              className="text-lg text-danger-dark"
              aria-hidden="true"
            />
          </span>
        </Tooltip>
      )}
      <IoMdArrowDropdown className="ml-1 flex-shrink-0 text-gray-dark" />
    </>
  );
  const nameCellPadding = isSheet ? 'pl-6 pr-7' : 'px-2';

  return (
    <tr
      {...{ [TASK_STEP_ROW_ATTR]: step.stepId }}
      className={MergeClassNames(
        // `group` drives the three-dot reveal. No row-level hover here —
        // outline/border on a <tr> doesn't reliably form a continuous
        // rectangle across real browsers (tried and dropped). Hover feedback
        // is field-level only (EditableCell's own border-on-hover).
        'group bg-white',
        // The sheet tints the whole row on hover, so the eye can follow a
        // product across to its numbers (OP-2556).
        isSheet && 'hover:bg-rowHover',
        isHighlighted && 'highlighted-row',
        isReordering && 'step-row-reordering',
        isDropTarget && 'step-row-drop-target'
      )}
      // Draggable only while the pointer rests on this row's handle, so
      // cells stay selectable. This is not the re-order gate, though — see
      // handleStepDragStart, which rejects anything whose press did not
      // begin on the grip.
      draggable={canReorder && armedDragStepId === step.stepId}
      onDragStart={(event) => {
        stepReorder.handleStepDragStart(event, task.taskId, step.stepId);
        // In the sheet, picture the product cells only, not the Task cell.
        if (isSheet && !event.defaultPrevented) setSheetRowDragImage(event);
      }}
      // dragenter as well as dragover: cancelling only the latter shows one
      // frame of the "no drop" cursor on entering a valid row.
      onDragEnter={(event) =>
        stepReorder.handleStepDragOver(event, task.taskId, step.stepId)
      }
      onDragOver={(event) =>
        stepReorder.handleStepDragOver(event, task.taskId, step.stepId)
      }
      onDragLeave={(event) =>
        stepReorder.handleStepDragLeave(event, step.stepId)
      }
      onDrop={(event) => stepReorder.handleStepDrop(event, task, step.stepId)}
      // Fires however the drag ends — dropped, cancelled with Escape, or
      // released outside the grid — so it is the one place that must leave
      // no state behind.
      onDragEnd={stepReorder.clearStepDrag}
      onClick={(event) => {
        event.stopPropagation();
        stepContextMenu.closeContextMenu();
      }}
      onContextMenu={(event) =>
        stepContextMenu.handleStepRowContextMenu(
          event,
          task.taskId,
          step.stepId
        )
      }
    >
      {leadingCell}
      <td
        className={MergeClassNames(
          // h-px lets the picker trigger's h-full fill the whole cell when
          // the row is taller than the name (a tall Task cell beside it), so
          // it opens from anywhere in the cell, like the value cells.
          'relative h-px border-l border-r p-0',
          inputNameCellBorder
        )}
      >
        {/* Sibling of the Popover, not a child of its trigger — inside it,
            clicking the grip would open the input picker. Positioned in the
            wrapper's pl-6 lane, left of the grid's own border, so the Input
            name column stays purely the product name and nothing shifts
            when a filter makes the grip come or go.

            Biased toward the row rather than centred in that lane
            (justify-end + pr-0.5): proximity is what tells a user which row
            the control belongs to, so the gap to the grid is deliberately
            smaller than the gap to the green rail. */}
        {canReorder && (
          <div
            className={MergeClassNames(
              'absolute inset-y-0 z-10 flex w-6 items-center',
              isSheet ? 'left-0 justify-center' : '-left-6 justify-end pr-0.5'
            )}
          >
            <RowGripButton
              label={`${TASKS_GRID_REORDER_STEP_ARIA_LABEL_PREFIX} ${step.item}`}
              isActive={isReordering}
              onActivate={(event) => {
                event.preventDefault();
                event.stopPropagation();
                stepReorder.startReorder(task.taskId, step.stepId);
              }}
              onArmDrag={(armed) =>
                stepReorder.setStepDragArmed(step.stepId, armed)
              }
              onPressStart={() => stepReorder.setDragOrigin(step.stepId)}
            />
          </div>
        )}
        <Popover
          trigger="click"
          placement="right"
          autoAdjustOverflow
          destroyOnHidden
          getPopupContainer={() => document.body}
          open={isPickerOpen}
          onOpenChange={(open) => {
            stepContextMenu.closeContextMenu();
            if (!open) {
              setEditingStepInputId(null);
              stepContextMenu.closeSeedTargetEdit();
              return;
            }
            setEditingStepInputId(!hasAmbiguousStepIds ? step.stepId : null);
          }}
          classNames={{
            root: 'step-input-selector-popover',
          }}
          content={
            isEditingSeedTarget && step.stepType === InputStepType.SeedDress ? (
              <div className="w-72 rounded-md bg-white p-3">
                <div className="mb-2 text-base font-semibold text-charcoal">
                  {CHANGE_SEED_TARGET_MENU_LABEL}
                </div>
                <SeedTargetList
                  task={task}
                  highlightedStepId={step.linkedStepId}
                  onSelect={(linkedStepId) => {
                    commitStepField(task, step.stepId, { linkedStepId });
                    stepContextMenu.closeSeedTargetEdit();
                  }}
                />
              </div>
            ) : (
              <StepInputSelector
                taskType={task.taskType}
                initialCategoryKey={stepInputCategoryKey}
                // A Production task's cost changes to another Operation item
                // only; the product has its own picker (OP-2399).
                categoryKeys={
                  isProductionCostStep(step, task) && stepInputCategoryKey
                    ? [stepInputCategoryKey]
                    : undefined
                }
                initialSearchText={step.item}
                initialCropGlobalId={
                  step.stepType === InputStepType.Seed
                    ? step.globalId
                    : undefined
                }
                initialCropName={
                  step.stepType === InputStepType.Seed ? step.item : undefined
                }
                initialVarietyGlobalId={
                  step.stepType === InputStepType.Seed
                    ? step.globalId2
                    : undefined
                }
                initialVarietyName={
                  step.stepType === InputStepType.Seed ? step.item2 : undefined
                }
                initialSeedDressStep={
                  step.stepType === InputStepType.SeedDress ? step : undefined
                }
                onClose={() => setEditingStepInputId(null)}
                onSelect={(selection) =>
                  handleStepInputChange(task, step.stepId, selection)
                }
                task={task}
                enableAddRecord
                lockedCategoryReason={seedLinkedTypeChangeReason}
              />
            )
          }
        >
          {/* Focusable, so Tab reaches the name and Enter or Space opens the
              picker, as the value cells do. */}
          <div
            ref={nameTriggerRef}
            onPointerMove={() => setIsHoverSuppressed(false)}
            role="button"
            tabIndex={hasAmbiguousStepIds ? -1 : 0}
            aria-haspopup="dialog"
            aria-expanded={isPickerOpen}
            className={MergeClassNames(
              'flex h-full w-full cursor-pointer items-center border border-transparent py-1 outline-none focus-visible:border-success-dark',
              !isHoverSuppressed && 'hover:z-10 hover:border-success',
              nameCellPadding
            )}
            onKeyDown={(event) => {
              if (
                (event.key === 'Enter' || event.key === ' ') &&
                !isPickerOpen &&
                !hasAmbiguousStepIds
              ) {
                event.preventDefault();
                stepContextMenu.closeContextMenu();
                setEditingStepInputId(step.stepId);
              }
            }}
          >
            {nameCellContent}
          </div>
        </Popover>
        {isSheet && (
          <div className="absolute inset-y-0 right-0 z-10 flex w-6 items-center justify-center">
            {kebabButton}
          </div>
        )}
      </td>
      <td
        className={MergeClassNames('relative border-r p-0', cellBorder)}
        {...{ [STEP_NAV_COL_ATTR]: STEP_NAV_COL_RATE }}
      >
        <div className="absolute inset-0">
          {isOperationRow ? (
            <div
              className={MergeClassNames(
                'flex h-full w-full items-center border border-transparent px-2 py-1 font-medium',
                readOnlyFill
              )}
            >
              {isOtherCostRow || isYieldLinkedStep(step, task)
                ? blankValue
                : rate === null
                  ? ' '
                  : rate}
            </div>
          ) : (
            <NumericInputField
              className="h-full w-full"
              inputClassName="h-full w-full"
              min={0}
              inclusiveMin
              allowEmpty
              disabled={hasAmbiguousStepIds}
              value={rate}
              displayValue={rate === null ? ' ' : rate}
              onArrowNavigate={focusAdjacentStepCell}
              onCommit={(value) =>
                commitStepField(task, step.stepId, { rate: value })
              }
            />
          )}
        </div>
      </td>
      {sheetColumns ? (
        <>
          {sheetColumns.rateUnit && rateUnitCell}
          {sheetColumns.required && requiredCell}
          {sheetColumns.costPerUnit && costPerUnitCell}
          {sheetColumns.costPerHa && costPerHaCell}
          {trailingCell}
        </>
      ) : (
        // The original layout folds columns for width the same way; their
        // values move under the product name (foldedColumns).
        <>
          {!foldedColumns?.rateUnit && rateUnitCell}
          {!foldedColumns?.costPerUnit && costPerUnitCell}
          {!foldedColumns?.required && requiredCell}
          {costPerHaCell}
        </>
      )}
    </tr>
  );
};

export default TaskStepRow;
